import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@h2j-data-team/ui";

import type { QlikAuthMethod } from "@repo/shared";

const QLIK_DOCS_URL = "https://qlik.dev/authenticate/oauth/create-oauth-client/";

const OAUTH_SCOPES = ["admin.apps", "admin.spaces:read", "admin.users:read"] as const;

type ConnectionHelpAsideProps = {
  authMethod: QlikAuthMethod;
};

export function ConnectionHelpAside({ authMethod }: ConnectionHelpAsideProps) {
  const { t } = useTranslation("connections");
  const isOauth = authMethod === "oauth_client_credentials";
  const whatItems = t("aside.whatGetsRead.items", { returnObjects: true }) as string[];

  return (
    <div className="flex flex-col gap-4 self-start lg:sticky lg:top-0">
      <Card>
        <CardHeader>
          <CardTitle asChild>
            <h2>{t("aside.whatGetsRead.title")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc space-y-1 pl-5 text-sm text-dim">
            {Array.isArray(whatItems) ? whatItems.map((item) => <li key={item}>{item}</li>) : null}
          </ul>
        </CardContent>
      </Card>

      {isOauth ? (
        <Card>
          <CardHeader>
            <CardTitle asChild>
              <h2>{t("aside.scopes.title")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="flex flex-col items-start gap-1.5">
              {OAUTH_SCOPES.map((scope) => (
                <li key={scope}>
                  <Badge variant="outline" className="font-mono">
                    {scope}
                  </Badge>
                </li>
              ))}
            </ul>
            <Button asChild variant="link" size="compact" className="px-0">
              <a href={QLIK_DOCS_URL} target="_blank" rel="noopener noreferrer">
                {t("aside.scopes.openDocumentation")}
                <ExternalLink />
              </a>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {isOauth ? (
        <Card>
          <CardHeader>
            <CardTitle asChild>
              <h2>{t("aside.howTo.title")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal space-y-1.5 pl-5 text-xs text-dim">
              <li>{t("form.oauthHowTo.step1")}</li>
              <li>
                {t("form.oauthHowTo.step2Label")}{" "}
                <span className="font-medium text-ink">{t("form.oauthHowTo.step2Value")}</span>
              </li>
              <li>
                {t("form.oauthHowTo.step3Label")}{" "}
                <span className="font-medium text-ink">{t("form.oauthHowTo.step3Value")}</span>
              </li>
              <li>
                {t("form.oauthHowTo.step4Label")}{" "}
                <span className="font-medium text-ink">{t("form.oauthHowTo.step4Value")}</span>{" "}
                {t("form.oauthHowTo.step4Suffix")}
              </li>
              <li>{t("form.oauthHowTo.step5")}</li>
              <li>{t("form.oauthHowTo.step6Prefix")}</li>
              <li>
                {t("form.oauthHowTo.step7Prefix")}{" "}
                <span className="font-medium text-ink">{t("form.oauthHowTo.step7Value")}</span>
              </li>
              <li>{t("form.oauthHowTo.step8")}</li>
            </ol>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
